import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserRole } from "./UserRoleContext";
import { get as apiGet } from "../services/api";

const BarContext = createContext();

export const useBar = () => {
  const context = useContext(BarContext);
  if (!context) {
    throw new Error("useBar must be used within a BarProvider");
  }
  return context;
};

export const BarProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [selectedBar, setSelectedBar] = useState(null);
  const [userBars, setUserBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [barShows, setBarShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(false);
  const [showsError, setShowsError] = useState(null);

  // Fetch user's bars
  const fetchBars = async () => {
    if (
      !currentUser ||
      (userRole !== "bar_owner" && userRole !== "bar_admin")
    ) {
      setUserBars([]);
      setSelectedBar(null);
      setLoading(false);
      setBarShows([]);
      setShowsError(null);
      setShowsLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiGet("/bars");

      if (data.success && data.bars) {
        // Parse JSON fields for each bar
        const parsedBars = data.bars.map((bar) => ({
          ...bar,
          time_slots:
            typeof bar.time_slots === "string"
              ? bar.time_slots
              : JSON.stringify(bar.time_slots),
          indoor_photos:
            typeof bar.indoor_photos === "string"
              ? bar.indoor_photos
              : JSON.stringify(bar.indoor_photos),
          outdoor_photos:
            typeof bar.outdoor_photos === "string"
              ? bar.outdoor_photos
              : JSON.stringify(bar.outdoor_photos),
        }));

        setUserBars(parsedBars);

        // Try to restore selected bar from localStorage
        const savedBarId = localStorage.getItem("selectedBarId");
        const savedBar = parsedBars.find(
          (bar) => bar.bar_id === parseInt(savedBarId)
        );

        if (savedBar) {
          setSelectedBar(savedBar);
        } else if (parsedBars.length > 0) {
          // Auto-select first bar if no saved selection or saved bar doesn't exist
          setSelectedBar(parsedBars[0]);
          localStorage.setItem(
            "selectedBarId",
            parsedBars[0].bar_id.toString()
          );
        }
      } else {
        setError("Failed to fetch bars");
        setBarShows([]);
      }
    } catch (err) {
      console.error("Error fetching bars:", err);
      setError(err.message);
      setBarShows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchShowsForBar = useCallback(async (barId, options = {}) => {
    const silent = Boolean(options.silent);
    if (!barId) {
      setBarShows([]);
      setShowsError(null);
      setShowsLoading(false);
      return;
    }

    try {
      if (!silent) {
        setShowsLoading(true);
      }
      setShowsError(null);
      const data = await apiGet(`/shows/bar/${barId}`);

      if (data.success) {
        setBarShows(data.shows || []);
      } else {
        setShowsError(data.error || "Failed to fetch shows");
        setBarShows([]);
      }
    } catch (err) {
      console.error("Error fetching bar shows:", err);
      setShowsError(err.message);
      setBarShows([]);
    } finally {
      if (!silent) {
        setShowsLoading(false);
      }
    }
  }, []);

  // Refresh bars list
  const refreshBars = () => {
    fetchBars();
  };

  // Update selected bar and persist to localStorage
  const updateSelectedBar = (bar) => {
    setSelectedBar(bar);
    if (bar) {
      localStorage.setItem("selectedBarId", bar.bar_id.toString());
    } else {
      localStorage.removeItem("selectedBarId");
    }
  };

  // Fetch bars on mount or when user/role changes
  useEffect(() => {
    fetchBars();
  }, [currentUser, userRole]);

  useEffect(() => {
    if (selectedBar?.bar_id) {
      fetchShowsForBar(selectedBar.bar_id);
    } else {
      setBarShows([]);
      setShowsError(null);
      setShowsLoading(false);
    }
  }, [selectedBar?.bar_id, fetchShowsForBar]);

  const showsSyncDebounceRef = useRef(null);

  useEffect(() => {
    const onShowsSync = () => {
      const id = selectedBar?.bar_id;
      if (!id) return;
      if (showsSyncDebounceRef.current) {
        clearTimeout(showsSyncDebounceRef.current);
      }
      showsSyncDebounceRef.current = setTimeout(() => {
        showsSyncDebounceRef.current = null;
        fetchShowsForBar(id, { silent: true });
      }, 400);
    };
    const onJoinSync = () => {
      fetchBars();
      if (selectedBar?.bar_id) {
        fetchShowsForBar(selectedBar.bar_id);
      }
    };
    window.addEventListener("showsUpdated", onShowsSync);
    window.addEventListener("joinRequestsUpdated", onJoinSync);
    return () => {
      window.removeEventListener("showsUpdated", onShowsSync);
      window.removeEventListener("joinRequestsUpdated", onJoinSync);
      if (showsSyncDebounceRef.current) {
        clearTimeout(showsSyncDebounceRef.current);
        showsSyncDebounceRef.current = null;
      }
    };
  }, [selectedBar?.bar_id, fetchShowsForBar]);

  const refreshBarShows = useCallback(
    (barId, options) => {
      const idToRefresh = barId ?? selectedBar?.bar_id;
      if (idToRefresh) {
        fetchShowsForBar(idToRefresh, options);
      }
    },
    [fetchShowsForBar, selectedBar?.bar_id],
  );

  const value = {
    selectedBar,
    setSelectedBar: updateSelectedBar,
    userBars,
    loading,
    error,
    refreshBars,
    barShows,
    showsLoading,
    showsError,
    refreshBarShows,
  };

  return <BarContext.Provider value={value}>{children}</BarContext.Provider>;
};
